"use client";
import type * as React from "react";

import { clamp } from "@ui/lib/math";
import { cn } from "@ui/lib/utils";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

import type { IGradientWrapperInternalProps } from "./GradientWrapperInternal";

const GradientWrapperInternal = dynamic(() => import("./GradientWrapperInternal"), { ssr: false });

interface IGradientWrapperProps extends IGradientWrapperInternalProps {
  children?: React.ReactNode;
  tag?: "div" | "section";
}

const GradientWrapper = ({ className, tag, children, ...props }: IGradientWrapperProps) => {
  const Tag = tag || "div";
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [displayCanvas, setDisplayCanvas] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) return;
    let rafId: number = 0;

    if (!displayCanvas) {
      window.cancelAnimationFrame(rafId);
      canvasRef.current.style.opacity = `0`;
    } else {
      const target = window.innerHeight * 0.5;
      const start = window.innerHeight * 0.1;
      const onRaf = () => {
        if (!canvasRef.current) return;

        const scrollY = window.scrollY;
        if (scrollY >= start && scrollY <= target) {
          const progress = 1 - clamp((scrollY - start) / (target - start), 0, 1);
          canvasRef.current.style.transition = "none";
          canvasRef.current.style.opacity = `${progress}`;
        } else if (scrollY > target) {
          canvasRef.current.style.opacity = `0`;
        } else {
          canvasRef.current.style.opacity = `1`;
        }
        rafId = window.requestAnimationFrame(onRaf);
      };

      rafId = window.requestAnimationFrame(onRaf);
    }
    return () => {
      window.cancelAnimationFrame(rafId);
      if (canvasRef.current) canvasRef.current.style.opacity = `0`;
    };
  }, [displayCanvas]);

  useEffect(() => {
    const IO = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setDisplayCanvas(true);
        } else {
          setDisplayCanvas(false);
        }
      });
    });
    if (wrapperRef.current) {
      IO.observe(wrapperRef.current);
    }
    return () => {
      IO.disconnect();
    };
  }, [wrapperRef]);

  if (!props.gradient)
    return (
      <Tag className={cn("relative", className)} ref={wrapperRef} {...props}>
        {children}
      </Tag>
    );

  return (
    <Tag className={cn("relative", className)} ref={wrapperRef} {...props}>
      <div className='relative z-10'>{children}</div>
      <div className='fixed top-0 -z-10 h-full w-full opacity-0 transition-opacity duration-300' ref={canvasRef}>
        {displayCanvas && <GradientWrapperInternal displayCanvas={displayCanvas} {...props} />}
      </div>
    </Tag>
  );
};

GradientWrapper.displayName = "GradientWrapper";

export { GradientWrapper };
